import type { SessionSummary } from '@/src/dal/session-dal'
import { buildMeasurementRecordingAudioSource } from '@/src/dal/measurement-dal'

import type {
  VoiceAssessmentDetailPayload,
  VoiceAssessmentEntryRowStatus,
  VoiceAssessmentHistoryAccordionItem,
  VoiceAssessmentStatisticsData,
} from '../types/voice-assessment-statistics'
import { selectVoiceAssessmentSessions } from './select-voice-assessment-sessions'

export function toDateLabel(created: Date): string {
  return created.toISOString().slice(0, 10)
}

function buildA2DemoCompletedDetail(
  session: SessionSummary,
): VoiceAssessmentDetailPayload {
  // TODO(A2 demo): replace measurement id 44 with real completed-session measurement data from the API.
  const measure1RecordingUrl = buildMeasurementRecordingAudioSource({
    measurementId: 44,
  })

  return {
    id: String(session.id),
    completedAtIso: session.created.toISOString(),
    voiceFeelingRating: 3,
    voiceFeelingMax: 5,
    mentalStateKey: 'tired',
    singingScore: 87,
    comfortableRange: 'c1 - g2',
    pitchRange: 'ab1 - eb2',
    measure1RecordingUrl,
    measure1RecordingDurationLabel: '0:12',
    measure2RecordingDurationLabel: '0:12',
    measure1: {
      voiceFreedomRating: 5,
      voiceFreedomMax: 5,
      exerciseEaseRating: 2,
      exerciseEaseMax: 5,
      tensionRating: 2,
      tensionMax: 5,
      tensionPlacementLabelKey: 'jaw_tongue',
    },
    measure2: {
      voiceFreedomRating: 4,
      voiceFreedomMax: 5,
      exerciseEaseRating: 3,
      exerciseEaseMax: 5,
      tensionRating: 2,
      tensionMax: 5,
      tensionPlacementLabelKey: 'jaw_tongue',
    },
    personalReflection:
      'This is how it felt and it is because of this and that. Placeholder reflection until the backend persists assessments.',
  }
}

function toRowStatus(session: SessionSummary): VoiceAssessmentEntryRowStatus {
  const status = session.status
  if (status === 'PendingAnalysis') {
    return 'analyzing'
  }
  if (status === 'Completed') {
    return 'completed'
  }
  // `Active` and `Aborted` are both non-final from the history list's
  // perspective. A failed analyze also lands on `Aborted`; the analysis-ready
  // toast distinguishes that failure via the analyzing -> incomplete transition.
  return 'incomplete'
}

/**
 * Maps backend session summaries into the Statistics voice-assessment read model.
 * Only sessions whose definitions match the configured voice-assessment
 * definition ids are listed.
 */
export function mapBackendSessionsToVoiceAssessmentStatistics(
  sessions: SessionSummary[],
  sessionDefinitionIds: readonly number[],
  options = { exerciseCount: 1 },
): VoiceAssessmentStatisticsData {
  const sorted = selectVoiceAssessmentSessions(sessions, sessionDefinitionIds)

  if (sorted.length === 0) {
    return {
      status: 'empty',
      entries: [],
      voiceTypeKey: null,
    }
  }

  const entries: VoiceAssessmentHistoryAccordionItem[] = sorted.map(
    (session, index) => ({
      id: String(session.id),
      backendSessionId: session.id,
      dateLabel: toDateLabel(session.created),
      exerciseCount: options.exerciseCount,
      isLatest: index === 0,
      rowStatus: toRowStatus(session),
      detail:
        session.status === 'Completed'
          ? buildA2DemoCompletedDetail(session)
          : null,
    }),
  )

  const latest = entries[0]
  const topStatus: VoiceAssessmentStatisticsData['status'] =
    latest != null && latest.isLatest && latest.rowStatus === 'analyzing'
      ? 'analyzing'
      : 'ready'

  return {
    status: topStatus,
    entries,
    voiceTypeKey: null,
  }
}
